// The PUBLIC namespace (Module 09 §4.10, slice P3-C-1) — the only strings in
// this product that a person outside the tenant ever reads.
//
// It is its own namespace, not part of `sales`, and that is a decision rather
// than filing convenience:
//
//  1. AUDIENCE. Every other namespace is written for someone who works at the
//     factory and has been trained on the product. This one is written for a
//     procurement manager at a construction firm who has clicked a link in a
//     Telegram message and has never heard of us. The register is different —
//     no internal vocabulary at all (no "tier", no "margin", no "revision"),
//     short sentences, and every screen says what to DO next.
//  2. LEAK SURFACE. Keeping the customer-facing copy in one file makes "does
//     this page say anything internal?" a question somebody can answer by
//     reading one file, which is the property §4.10's no-cost/no-margin rule
//     needs on the words as well as on the payload.
//  3. LOCALE RULE. These strings are selected by the DOCUMENT's locale
//     (lib/public-view.publicRenderLocale), never by the browser's — see that
//     function's header for why, and for what happens to zh/fr placeholders.
//
// THE ONE THING THIS FILE MUST NEVER GAIN: a key that names an internal state
// (`pending_approval`, `off_list`, a guard percentage, a cost). The customer's
// five states are the five §4.10 pages, and nothing behind them is theirs.
//
// COUNTED NOUNS ARE NOT AGREED WITH THEIR NUMBER (D-030) — «Дней: 12», not
// three plural branches a flat dictionary cannot express.

import type { Translated } from "../../i18n-shape.js";
import { publicRuDict } from "./public.ru.js";

export const publicEn = {
  // The chromeless shell (public-shell.tsx).
  shell: {
    loading: "Loading…",
    retry: "Try again",
    /** Shown under a failure so a customer can quote something useful when
     * they ring their manager. */
    requestId: "Reference",
    poweredBy: "Powered by Assessly",
  },

  quote: {
    /** Rendered as `Offer Q-2026-00042`. */
    heading: "Offer {{quoteNo}}",
    forCustomer: "Prepared for {{customer}}",
    from: "From {{seller}}",
    contactManager: "Contact your manager",
    /** §4.10 promises the expired page "names whom to ask for", and with the
     * contact URL withheld for all of P3 the NAME is the whole of that
     * promise. A page that says "ask your manager" and never says who is
     * asking a customer to guess. */
    yourManager: "Your manager is {{name}}.",
    // --- the valid page -----------------------------------------------
    colItem: "Item",
    colQty: "Quantity",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    deliveryBy: "Delivery by {{date}}, confirmed when you accept",
    subtotal: "Subtotal",
    vatExclusive: "VAT {{rate}}%",
    vatInclusive: "Includes VAT {{rate}}%",
    total: "Total",
    validUntil: "Valid until {{date}}",
    daysLeft: "Days left: {{count}}",
    /** Day ZERO — the last day the offer can be accepted. There is no second
     * string for "expires today": one boundary, one sentence. */
    lastDay: "Last day to accept",
    note: "Note",
    /** The customer's own copy of the offer, the document that went out with
     * their link (D-288 ruling 4). Offered on the valid page and on the
     * accepted page, where it is their record of what they agreed to; never
     * on the expired or superseded page, where the old offer would invite
     * acting on it. */
    pdf: "Download this offer (PDF)",
    accept: "Accept this offer",
    decline: "Decline",
    accepting: "Accepting…",
    /** The one confirmation step before a legally meaningful press. */
    confirmTitle: "Accept this offer?",
    confirmBody: "We will reserve the goods and confirm your order straight away.",
    confirmYes: "Yes, accept",
    confirmNo: "Not yet",
    declineTitle: "Decline this offer?",
    declineBody: "You can tell us why — it helps us quote you better next time.",
    declineWhyPrice: "The price",
    declineWhyLeadTime: "The delivery time",
    declineWhyStock: "You need it sooner than we have it",
    declineWhyOther: "Something else",
    declineSkip: "Just decline",
    declineYes: "Decline the offer",
    declined: "Thank you — we have told your manager.",
    declineFailed: "We could not record that. Please try again.",
    acceptFailed: "We could not accept the offer just now. Please try again.",
    /** The §9.1 double-tap loser, and the already-used token. */
    acceptGoneTitle: "This offer was already accepted",
    // --- accepted (the success page and the already-accepted state) ----
    acceptedTitle: "Your order is confirmed",
    acceptedNumber: "Order {{orderNo}}",
    acceptedOn: "Accepted {{date}}",
    trackOrder: "Track your order",
    // --- expired -------------------------------------------------------
    expiredTitle: "This offer has expired",
    expiredOn: "It was valid until {{date}}.",
    /** THE OTHER WAY AN OFFER DIES (D-268 ruling 3): the manager withdrew it
     * on §4.5's bulk bar before its validity ran out, so there is no date to
     * state and `expiredOn` must not be rendered. Saying "it has expired" of a
     * quote whose `valid_until` is still a fortnight away invites the reply
     * "no it hasn't, look at the date" — and until this key existed the page
     * printed exactly that date. `expiredAsk` follows either title: the remedy
     * is the same one, and it is all this page can do. */
    withdrawnTitle: "This offer is no longer available",
    expiredAsk: "Ask your manager for a new offer — they can send one in a minute.",
    // --- superseded ----------------------------------------------------
    supersededTitle: "There is a newer version of this offer",
    supersededBody: "The prices on this page are out of date.",
    supersededOn: "A newer offer was sent on {{date}}.",
    supersededAsk: "Check that message, or ask your manager to resend it.",
    // --- not found -----------------------------------------------------
    notFoundTitle: "This link does not work",
    notFoundBody:
      "It may have been mistyped, or it may have been replaced. Check the message it came in, or ask the person who sent it.",
    // --- error (not a state of the offer) -------------------------------
    errorTitle: "We could not load this page",
    errorBody: "Something went wrong at our end. Your offer is unaffected — please try again.",
  },

  track: {
    heading: "Order {{orderNo}}",
    from: "From {{seller}}",
    statusHeading: "Progress",
    // The §4.10 timeline vocabulary — the customer's words for our states.
    stepConfirmed: "Confirmed",
    stepInProduction: "In production",
    stepReady: "Ready",
    stepShipped: "Shipped",
    stepDelivered: "Delivered",
    /** A step the order has not reached — announced to screen readers so the
     * timeline is not just a colour. */
    stepPending: "Not yet",
    stepDone: "Done",
    promiseDate: "Ready by {{date}}",
    /** §3.5d's stated absence: module 03 owns the date and is not built. */
    promisePending:
      "Your manager will confirm the date — we will show it here as soon as it is set.",
    colItem: "Item",
    colQty: "Quantity",
    colShipped: "Shipped",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    total: "Total",
    /** The honest statement under the timeline: the steps the order has not
     * reached are still to come, and a message arrives with each. Re-worded
     * with P3-D PR-D3 (D-286): Shipped and Delivered are reachable now, so
     * the sentence no longer implies that only Confirmed can light up. */
    stagesPending:
      "Updates appear here as your order moves. You will also get a message each time it does.",
    // --- shipments (P3-E PR-E2, D-292 ruling 12) --------------------------
    // Each shipped shipment: its number, when it left, the carrier and the
    // tracking reference when recorded, and when and how it was delivered.
    // No money and no internal reason — the payload carries neither.
    shipmentsHeading: "Shipments",
    shipmentNo: "Shipment {{no}}",
    shipmentShipped: "Shipped",
    shipmentCarrier: "Carrier",
    shipmentTrackingRef: "Tracking number",
    shipmentDelivery: "Delivery",
    shipmentOnTheWay: "On the way",
    shipmentDeliveredAt: "Delivered {{when}}",
    /** HOW the delivery was confirmed. `auto` is the plant's rule: nobody
     * confirmed within the set number of days, so it counts as delivered —
     * said plainly, so a customer who never pressed anything is not told they
     * did. */
    deliveredBy: {
      customer: "confirmed by you",
      carrier: "confirmed by the carrier",
      auto: "confirmed automatically",
    },
    // --- confirm received (§4.10, P3-D PR-D3, D-286) ---------------------
    // The customer's own press is the source of truth for delivery. Offered
    // exactly when the server says so (`can_confirm_received`, computed as
    // `status = 'shipped'`); the confirm step is the accept page's shape. No
    // key here names an internal state — "delivered" is the customer's word
    // for the step they can see on the timeline above.
    confirmReceivedTitle: "Have you received this order?",
    confirmReceived: "Yes, I received it",
    confirmReceivedAsk:
      "Confirm that the goods arrived. This tells your manager the order is complete.",
    confirmReceivedYes: "Confirm",
    confirmReceivedNo: "Not yet",
    confirmReceivedDone: "Thank you — marked as delivered.",
    /** A failed press. Not the page-level `errorBody`: the page loaded fine,
     * the one thing that did not land is the answer, and the remedy is the
     * same press again. */
    confirmReceivedFailed: "We could not record that. Please try again.",
    contactManager: "Message your manager",
    notFoundTitle: "This link does not work",
    notFoundBody:
      "It may have been mistyped. Check the message it came in, or ask the person who sent it.",
    errorTitle: "We could not load this page",
    errorBody: "Something went wrong at our end. Your order is unaffected — please try again.",
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type PublicDict = Translated<typeof publicEn>;

// ru is TRANSLATED (public.ru.ts) — the design partner's customers are Kazakh
// construction and energy firms whose procurement departments work in Russian,
// which makes this the one namespace where an untranslated locale is visible
// OUTSIDE the company. fr, zh, zh-Hant and hi are machine-translated in their
// own files (public.<locale>.ts), which src/locales/<locale>.ts imports so each
// stays in its locale's chunk. Their pages still render English until the
// locale leaves `placeholder` in src/i18n-completeness.ts
// (lib/public-view.publicRenderLocale).
export const publicRu: PublicDict = publicRuDict;
